import { useFilterParams } from "@/hooks/useFilterParams"
import { usePagination } from "@/hooks/usePagination"
import { FC, useEffect, useRef, useState } from "react"
import { useGetProductListLazyQuery } from "../../../../../graphql"
import Filter from "./components/Filter"
import List from "./components/List"
import "./index.less"

export type ProductBrand = "KM" | "other"

const DEFAULT_FILTERS: { [key: string]: string } = {
  selectedName: "All",
  selectedType: "All",
  selectedBenefit: "All",
  brand: "",
}

const ListBlock: FC = () => {
  const [getProductList] = useGetProductListLazyQuery()

  const [loading, setLoading] = useState<boolean>(false)
  const [exportLoading, setExportLoading] = useState<boolean>(false)

  const { filters, setFilters, filtersKey } = useFilterParams(DEFAULT_FILTERS)
  const { page, pageSize, setPage, setPageSize } = usePagination()

  const brand = filters.brand || "KM"

  const currentBrand = useRef<string | null>(null)

  const [list, setList] = useState<any>()
  const [listForExport, setListForExport] = useState<any>()
  const [totalItems, setTotalItems] = useState<number>(0)

  const downloadBtnRef = useRef<HTMLButtonElement & { link: HTMLAnchorElement | null }>(null)

  const setBrand = (brand: ProductBrand) => {
    setFilters({ brand })
  }

  const createQueryFilters = (isPaginationDisabled = false) => {
    return {
      variables: {
        input: {
          page: isPaginationDisabled ? 1 : page,
          pageSize: isPaginationDisabled ? 1000 : pageSize,
          filter: {
            brand,
            ...(filters.selectedName !== "All" ? { search: filters.selectedName } : {}),
            ...(filters?.selectedBenefit !== "All" ? { benefitId: +filters.selectedBenefit } : {}),
            ...(filters?.selectedType !== "All" ? { typeId: +filters.selectedType } : {}),
            orderBy: {
              isReversed: false,
            },
          },
        },
      },
    }
  }

  const createList = async (isForExport = false) => {
    setLoading(true)
    if (list && !isForExport) {
      setList(undefined)
    }

    const newList = await getProductList({
      fetchPolicy: "network-only",
      ...createQueryFilters(isForExport),
    })

    const totalItems = newList?.data?.products?.total || 0
    const finalData = newList?.data?.products?.data

    if (finalData && !isForExport) {
      setTotalItems(totalItems!)
      setList(finalData)
      setLoading(false)
      return
    } else {
      setLoading(false)
      return finalData
    }
  }

  const createExportList = async () => {
    setExportLoading(true)
    const list = await createList(true)

    let listForExport =
      list?.map((item: any) => [
        item?.title || "-",
        item?.descriptionTitle || "-",
        item?.type?.name || "-",
        ...(brand === "KM" ? [item?.benefit?.name || "-"] : []),
      ]) || []
    listForExport = listForExport?.map(item => item.filter(entity => entity !== null))
    listForExport.unshift(["NAME", "TITLE", "TYPE", ...(brand === "KM" ? ["REGIMENTS"] : [])])

    setExportLoading(false)
    setListForExport(listForExport)
  }

  const createSpecificFiltersList = () =>
    filters.selectedName !== "All"
      ? [{ option: filters.selectedName, backgroundColor: "rgba(215, 163, 171, 0.15)", color: "#D7A3AB" }]
      : []

  const specificFilters = createSpecificFiltersList()

  useEffect(() => {
    listForExport && downloadBtnRef.current!.link!.click()
  }, [listForExport])

  useEffect(() => {
    setListForExport(undefined)
    createList()
  }, [pageSize, page, filtersKey, brand])

  useEffect(() => {
    if (currentBrand.current === null) {
      currentBrand.current = brand
      return
    }

    if (brand === currentBrand.current) return

    currentBrand.current = brand

    setFilters({
      selectedName: "All",
      selectedType: "All",
      selectedBenefit: "All",
      page: "1",
      pageSize: "10",
    })
  }, [brand])

  return (
    <div className={"block-wrapper"}>
      <Filter
        {...{
          brand,
          filters,
          setFilters,
          setBrand,
          specificFilters,
        }}
      />
      <List
        {...{
          list,
          page,
          brand,
          loading,
          setPage,
          filters,
          pageSize,
          setFilters,
          totalItems,
          setLoading,
          setPageSize,
          exportLoading,
          listForExport,
          downloadBtnRef,
          createExportList,
          createQueryFilters,
        }}
      />
    </div>
  )
}

export default ListBlock
